import { useEffect, useRef } from 'react';

/** Natives that act on Escape themselves — an input clears, a select reverts. */
const EDITABLE_TAG_NAMES: readonly string[] = [
  'INPUT',
  'TEXTAREA',
  'SELECT',
] as const;

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (EDITABLE_TAG_NAMES.includes(target.tagName)) {
    return true;
  }
  // Attribute as well as property: jsdom never computes isContentEditable.
  const contentEditable = target.getAttribute('contenteditable');
  return (
    target.isContentEditable ||
    contentEditable === '' ||
    contentEditable === 'true'
  );
};

export interface UseEscapeKeyOptions {
  enabled: boolean;
  onEscape: () => void;
}

export function useEscapeKey(options: UseEscapeKeyOptions): void {
  const { enabled, onEscape } = options;

  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isEditableTarget(event.target)) {
        return;
      }
      onEscapeRef.current();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled]);
}
